import React from "react";

// Must match app/helpers/outils_helper.rb (img_deurenberg / img_category).
const DEFAULTS = { gender: "male", weight: "70", height: "170", age: "30" };

const round1 = (n) => Math.round(n * 10) / 10;
const fr = (n) => String(round1(n)).replace(".", ",");

const imgDeurenberg = (weight, height, age, sex) => {
    const m = height / 100;
    const imc = weight / (m * m);
    return round1(1.2 * imc + 0.23 * age - 10.8 * (sex === "male" ? 1 : 0) - 5.4);
};

const LABELS = ["Graisse essentielle", "Athlète", "Forme (fitness)", "Acceptable", "Élevé (obésité)"];
const imgCategory = (img, sex) => {
    const thresholds = sex === "male" ? [5, 13, 17, 24] : [13, 20, 24, 31];
    const idx = thresholds.findIndex((t) => img <= t);
    return LABELS[idx === -1 ? 4 : idx];
};

function GenderButton({ gender, selected, onClick }) {
    const cls = selected
        ? "bg-slate-800 hover:bg-slate-700 text-white shadow-md"
        : "bg-stone-100 hover:bg-stone-50 text-gray-900";
    const path = gender === "male"
        ? "M823-829v255h-94v-93L573-512q19 30 28.5 63.5T611-380q0 99-69 168t-168 69q-99 0-168-69t-69-168q0-99 69-168t168-69q36 0 68.5 10t62.5 29l156-156h-92v-95h254ZM374.202-523Q314-523 272.5-481.702t-41.5 101.5Q231-320 272.298-278.5t101.5 41.5Q434-237 475.5-278.298t41.5-101.5Q517-440 475.702-481.5t-101.5-41.5Z"
        : "M433-92v-80h-80v-88h80v-89q-83-16-136.5-81.206T243-580q0-98.931 69.484-167.966Q381.968-817 480-817t167.516 69.034Q717-678.931 717-580q0 84.588-53.5 149.794Q610-365 527-349v89h80v88h-80v80h-94Zm46.798-345Q540-437 581.5-478.298t41.5-101.5Q623-640 581.702-681.5t-101.5-41.5Q420-723 378.5-681.702t-41.5 101.5Q337-520 378.298-478.5t101.5 41.5Z";
    return (
        <button
            onClick={onClick}
            className={`cursor-pointer ${cls} active:bg-slate-600 active:text-white focus:ring focus:ring-slate-300 w-1/2 rounded-lg px-2 sm:px-4 py-4 sm:py-6`}
            aria-pressed={selected}
        >
            <svg className="fill-current w-full" xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 -960 960 960" width="48" aria-hidden="true">
                <path d={path} />
            </svg>
            <div className="text-sm sm:text-base font-semibold">{gender === "male" ? "Homme" : "Femme"}</div>
        </button>
    );
}

function NumberField({ id, label, unit, value, onChange }) {
    return (
        <div>
            <label htmlFor={id} className="text-sm sm:text-base mb-2 block cursor-default">{label}</label>
            <div className="flex items-center gap-2">
                <input id={id} type="text" inputMode="numeric"
                    className="py-2 w-16 sm:w-20 text-center px-2 font-bold rounded-lg text-base sm:text-lg focus:outline-none focus:ring focus:ring-slate-300"
                    value={value} onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))} />
                <span className="text-base sm:text-xl font-bold cursor-default">{unit}</span>
            </div>
        </div>
    );
}

export default function App() {
    const [gender, setGender] = React.useState(DEFAULTS.gender);
    const [weight, setWeight] = React.useState(DEFAULTS.weight);
    const [height, setHeight] = React.useState(DEFAULTS.height);
    const [age, setAge] = React.useState(DEFAULTS.age);

    const w = parseInt(weight, 10);
    const h = parseInt(height, 10);
    const a = parseInt(age, 10);
    const valid = w > 0 && h >= 120 && h <= 250 && a > 0 && a < 120;

    const img = valid ? imgDeurenberg(w, h, a, gender) : null;
    const shown = img !== null ? Math.max(0, img) : null;
    const fatKg = shown !== null ? round1((shown / 100) * w) : null;
    const leanKg = shown !== null ? round1(w - fatKg) : null;

    return (
        <>
            <div className="flex w-full text-center gap-2 sm:gap-4">
                <GenderButton gender="male" selected={gender === "male"} onClick={() => setGender("male")} />
                <GenderButton gender="female" selected={gender === "female"} onClick={() => setGender("female")} />
            </div>

            <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg flex flex-wrap gap-4 sm:gap-6">
                <NumberField id="mg-weight" label="Votre poids" unit="kg" value={weight} onChange={setWeight} />
                <NumberField id="mg-height" label="Votre taille" unit="cm" value={height} onChange={setHeight} />
                <NumberField id="mg-age" label="Votre âge" unit="ans" value={age} onChange={setAge} />
            </div>

            {shown !== null && (
                <>
                    <div className="p-4 my-2 sm:my-4 rounded-lg w-full bg-gradient-to-b from-amber-900 to-amber-700 text-white text-center">
                        <div className="text-sm text-amber-200">Indice de masse grasse (IMG)</div>
                        <div className="text-4xl sm:text-5xl font-bold my-1">{fr(shown)} <span className="text-xl font-semibold">%</span></div>
                        <div className="text-xs text-amber-100">Catégorie : {imgCategory(shown, gender)}</div>
                    </div>

                    <div className="flex items-stretch gap-2 sm:gap-4">
                        <div className="w-1/2 bg-stone-100 rounded-lg px-4 py-3 text-center">
                            <div className="text-xs text-gray-600">Masse grasse</div>
                            <div className="font-bold text-lg sm:text-xl text-amber-800">{fr(fatKg)} kg</div>
                        </div>
                        <div className="w-1/2 bg-stone-100 rounded-lg px-4 py-3 text-center">
                            <div className="text-xs text-gray-600">Masse maigre</div>
                            <div className="font-bold text-lg sm:text-xl text-gray-900">{fr(leanKg)} kg</div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
